import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Clapperboard, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatRunningTime } from '../../lib/treatmentHelpers';
import { totalDurationSeconds } from '../../lib/treatmentShots';


/**
 * The Treatments tab of the Briefs section: the list, and the dialog that
 * creates one. The page header owns the "New" button,
 * so the dialog's open state comes in as props.
 */
export default function TreatmentsList({ dialogOpen, onDialogOpenChange }) {
  const { treatments, treatmentsLoading, fetchTreatments, createTreatment, deleteTreatment, jobs, fetchJobs } = useAppStore();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '' });

  useEffect(() => {
    fetchTreatments();
    fetchJobs();
  }, [fetchTreatments, fetchJobs]);

  const jobLabel = (jobId) => {
    const job = jobs.find((j) => j.id === jobId);
    return job ? `Job ${job.job_number || job.project_name}` : 'Job';
  };

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast.error('Give the shot list a title');
      return;
    }
    setCreating(true);
    try {
      const treatment = await createTreatment({
        title: form.title.trim(),
      });
      toast.success('Shot list created');
      onDialogOpenChange(false);
      setForm({ title: '' });
      navigate(`/treatments/${treatment.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create shot list');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (e, treatment) => {
    e.stopPropagation();
    if (!window.confirm(`Delete shot list "${treatment.title}"? This cannot be undone.`)) return;
    try {
      await deleteTreatment(treatment.id);
      toast.success('Shot list deleted');
    } catch (err) {
      toast.error('Failed to delete shot list');
    }
  };

  return (
    <>
      <Card className="rounded-cards overflow-hidden">
        <CardContent className="p-0">
          {treatmentsLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : treatments.length === 0 ? (
            <div className="text-center py-20">
              <Clapperboard className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No shot lists yet. Break a project down shot by shot.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Shot list</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Shots</TableHead>
                  <TableHead>Linked to</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {treatments.map((t) => {
                  const shots = t.shots || [];
                  const topLevel = shots.filter((s) => !s.parent_id).length;
                  const refs = shots.reduce((n, s) => n + (s.references?.length || 0), 0);
                  return (
                    <TableRow
                      key={t.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/treatments/${t.id}`)}
                      data-testid={`treatment-row-${t.id}`}
                    >
                      <TableCell className="font-medium">{t.title}</TableCell>
                      <TableCell className="text-muted-foreground">{t.client || '—'}</TableCell>
                      <TableCell>
                        {topLevel}
                        <span className="text-muted-foreground"> · {formatRunningTime(totalDurationSeconds(shots))} · {refs} refs</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {t.job_id && <Badge variant="secondary" className="text-xs">{jobLabel(t.job_id)}</Badge>}
                          {!t.job_id && <span className="text-muted-foreground text-xs">—</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Delete ${t.title}`}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={(e) => handleDelete(e, t)}
                          data-testid={`delete-treatment-${t.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={onDialogOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New shot list</DialogTitle>
            <DialogDescription>
              A shot-by-shot breakdown to share with your client. You can link it to a job once it exists.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="treatment-title">Title *</Label>
              <Input
                id="treatment-title"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Saltwater — brand film"
                data-testid="treatment-title-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onDialogOpenChange(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-treatment-submit">
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              Create shot list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
